import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
  SortOrder,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { Badge } from "types/badge";
import { useMutation, useQuery } from "@apollo/client";
import { GET_BADGES } from "graphql/badge/badge.query";
import { DELETE_BADGE } from "graphql/badge/badge.mutation";
import CreateOrEditBadgeModal from "modals/CreateOrEditBadgeModal/CreateOrEditBadgeModal";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { ResponsiveButton } from "components/Button";
import { useNotifications } from "hooks/useNotifications";
import { Box, Avatar } from "@mui/material";

export default function BadgeList() {
  const [createBadgeModal, setCreateBadgeModal] = useState(false);
  const [deleteBadgeModal, setDeleteBadgeModal] = useState(false);
  const [badgeIdToDelete, setBadgeIdToDelete] = useState("");
  const [badges, setBadges] = useState([] as Badge[]);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [deleteBadge] = useMutation(DELETE_BADGE);
  const { loading, error, data, refetch } = useQuery(GET_BADGES);
  const { notify } = useNotifications();

  useEffect(() => {
    const list = (data?.badge as Badge[]) || [];
    const sorted = [...list].sort(
      (a, b) => (a.criteria_value ?? 0) - (b.criteria_value ?? 0)
    );
    setBadges(sorted);
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        const badgeToEdit = badges?.find((badge) => badge.badge_id === id);
        if (badgeToEdit) {
          setSelectedBadge(badgeToEdit);
          setCreateBadgeModal(true);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setBadgeIdToDelete(id);
        setDeleteBadgeModal(true);
        break;
      default:
        break;
    }
  };

  const handleCreateBadgeModalOpen = () => {
    setCreateBadgeModal(true);
  };

  const handleCreateOrEditBadgeChange = () => {
    if (selectedBadge !== null) {
      setSelectedBadge(null);
    }
    setCreateBadgeModal(false);
    refetch().then();
  };

  const handleDelete = async () => {
    if (!badgeIdToDelete || badgeIdToDelete === "") {
      return;
    }
    await deleteBadge({ variables: { id: badgeIdToDelete } });
    await refetch();
    await refetch();
    setBadgeIdToDelete("");
    notify({
      message: "Badge deleted successfully",
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Badges"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => handleCreateBadgeModalOpen()}
          icon={<Add />}
        >
          New Badge
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={badges}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "icon",
            label: "Icon",
            value: (row: Badge) => (
              <Avatar
                src={row.icon_url || undefined}
                alt={row.name}
                sx={{ width: 40, height: 40 }}
              >
                {row.name.charAt(0)}
              </Avatar>
            ),
          },
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "description",
            label: "Description",
            value: "description",
          },
          {
            id: "criteria_type",
            label: "Criteria Type",
            value: "criteria_type",
          },
          {
            id: "criteria_value",
            label: "Criteria Value",
            value: "criteria_value",
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"badge_id"}
        sortDefault={SortOrder.asc}
        sortDefaultField={"criteria_value"}
      />

      {createBadgeModal && (
        <CreateOrEditBadgeModal
          handleCreateOrEditBadgeChange={handleCreateOrEditBadgeChange}
          createOrEditBadgeModal={createBadgeModal}
          handleCreateOrEditBadgeModalDismiss={() => {
            setCreateBadgeModal(false);
            if (selectedBadge !== null) {
              setSelectedBadge(null);
            }
          }}
          badge={selectedBadge}
        />
      )}

      {deleteBadgeModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this badge?"}
          show={deleteBadgeModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteBadgeModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
